import { ApiError } from '@worklogix/web-common';
import { useState } from 'react';

import { verifyDeviceToken } from '../api';

import { setDeviceContext, setDeviceToken } from './deviceToken';

/**
 * One-time device enrollment. An admin mints a device token in web-app's
 * KiosksTab (shown once, bound to a department) and pastes it here. We validate
 * it against `GET /kiosk/current` before persisting, so a bad paste never leaves
 * the device in a half-paired state. This is device+PIN auth — there is NO Entra
 * sign-in anywhere in this app.
 */
export function PairingScreen(): JSX.Element {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pair = (): void => {
    const trimmed = token.trim();
    if (trimmed === '') {
      setError('Paste the enrollment token from your administrator.');
      return;
    }
    setBusy(true);
    setError(null);
    void verifyDeviceToken(trimmed)
      .then((current) => {
        // Valid — persist token + department context. The token change re-renders
        // the app into the clock screen (useDeviceToken).
        setDeviceContext({
          departmentId: current.departmentId,
          departmentName: current.departmentName,
        });
        setDeviceToken(trimmed);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setError('That token was rejected. Check with your administrator for a fresh one.');
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Could not reach the server. Check the network connection and try again.');
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="kiosk">
      <div className="card pairing">
        <h1>Pair this clock</h1>
        <p>
          Enter the enrollment token from the WorkLogix admin console (Kiosks tab). This binds the
          tablet to one department.
        </p>
        <textarea
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
          }}
          placeholder="Paste device enrollment token"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          disabled={busy}
        />
        <button type="button" onClick={pair} disabled={busy}>
          {busy ? 'Pairing…' : 'Pair device'}
        </button>
        {error !== null && <p className="pairing__error">{error}</p>}
      </div>
    </div>
  );
}
